import {
  forwardRef,
  useId,
  type ComponentProps,
  type ComponentType,
  type ForwardRefExoticComponent,
  type RefAttributes,
  type SelectHTMLAttributes,
} from 'react'
import clsx from 'clsx'
import PhoneInput, { getCountryCallingCode, type Country } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { ChevronDown } from '@/assets/icons'
import { InlineMessage } from '@/components/inline-message/InlineMessage'

// Version 3.4.17 forwards this ref to the native input at runtime, but its declaration still
// describes the pre-wrapper class component.
const PhoneInputWithRef = PhoneInput as unknown as ForwardRefExoticComponent<ComponentProps<typeof PhoneInput> & RefAttributes<HTMLInputElement>>

export type PhoneNumberInputProps = {
  /** Controlled E.164 phone number, or an empty string when no number has been entered. */
  value: string
  onChange: (value: string) => void
  /** Initially selected country. The owning flow decides whether to provide one. */
  defaultCountry?: Country
  disabled?: boolean
  errorMessage?: string
  /** Visible field label. Pass `null` when the surrounding UI already labels the field. */
  label?: string | null
  placeholder?: string
  required?: boolean
}

/**
 * Shared presentation for collecting an international phone number.
 *
 * The owning form remains responsible for validation and submission policy. This component only
 * adapts the third-party input to Paxton styling, accessibility, and a controlled string contract.
 */
export const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(function PhoneNumberInput(
  { value, onChange, defaultCountry, disabled = false, errorMessage, label = 'Phone number', placeholder, required = false },
  ref
) {
  const generatedId = useId()
  const inputId = `phone-number-${generatedId}`
  const errorId = errorMessage ? `${inputId}-error` : undefined
  const accessibleLabel = label || 'Phone number'

  return (
    <div className="flex w-full flex-col gap-1" data-ds-component="PhoneNumberInput">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text-secondary">
          {label}
          {required && (
            <span className="ml-[2px] text-red-700" aria-hidden>
              *
            </span>
          )}
        </label>
      )}

      <PhoneInputWithRef
        ref={ref}
        id={inputId}
        value={value}
        onChange={(nextValue) => onChange(nextValue ?? '')}
        defaultCountry={defaultCountry}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        initialValueFormat="national"
        focusInputOnCountrySelection={false}
        countrySelectComponent={CountryCodeSelect}
        countrySelectProps={{
          'aria-label': `${accessibleLabel} country`,
        }}
        numberInputProps={{
          'aria-label': label ? undefined : accessibleLabel,
          className: 'h-full border-0 bg-transparent p-3 text-inherit focus:ring-0 focus:outline-hidden disabled:cursor-not-allowed',
        }}
        aria-invalid={errorMessage ? true : undefined}
        aria-describedby={errorId}
        className={clsx(
          "relative h-[54px] w-full overflow-hidden rounded bg-surface-field text-text-primary after:pointer-events-none after:absolute after:inset-0 after:z-20 after:rounded after:ring-1 after:ring-inset after:content-['']",
          disabled
            ? 'after:ring-border-strong'
            : errorMessage
            ? 'after:ring-border-field-error focus-within:after:ring-border-field-error'
            : 'after:ring-border-field focus-within:after:ring-border-field-active',
          disabled && 'cursor-not-allowed bg-surface-subtle text-brand-neutral-700'
        )}
      />

      {errorMessage && (
        <div id={errorId}>
          <InlineMessage variant="error">{errorMessage}</InlineMessage>
        </div>
      )}
    </div>
  )
})

type CountryOption = {
  value?: Country
  label: string
  divider?: boolean
}

type CountryCodeSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> & {
  value?: Country
  onChange: (value?: Country) => void
  options: CountryOption[]
  readOnly?: boolean
  iconComponent: ComponentType<CountryIconProps>
}

type CountryIconProps = {
  country?: Country
  label: string
  'aria-hidden'?: boolean
}

function CountryCodeSelect(props: CountryCodeSelectProps) {
  const { value, onChange, options, disabled, readOnly, iconComponent: CountryIcon, ...selectProps } = props
  const selectedOption = options.find((option) => !option.divider && option.value === value)

  return (
    <div className="relative flex w-[6.5rem] shrink-0 self-stretch items-center gap-1.5 border-r border-border-strong px-2.5">
      <select
        {...selectProps}
        value={value ?? 'ZZ'}
        onChange={(event) => onChange(event.target.value === 'ZZ' ? undefined : (event.target.value as Country))}
        disabled={disabled || readOnly}
        className="absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.divider ? 'divider' : option.value ?? 'ZZ'} value={option.divider ? 'divider' : option.value ?? 'ZZ'} disabled={option.divider}>
            {option.label}
          </option>
        ))}
      </select>

      {selectedOption && <CountryIcon aria-hidden country={value} label={selectedOption.label} />}
      {value && (
        <span className="text-sm text-text-secondary" aria-hidden>
          +{getCountryCallingCode(value)}
        </span>
      )}
      <ChevronDown className="ml-auto h-3.5 w-3.5 text-brand-neutral-500" aria-hidden />
    </div>
  )
}
